import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControlLabel, Grid, IconButton, MenuItem, Stack, Switch, TextField, Typography,
} from "@mui/material";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { shippingApi } from "../../api/shipping";

const CARRIERS = [
  { value: "correo_argentino", label: "Correo Argentino" },
  { value: "andreani", label: "Andreani" },
  { value: "propio", label: "Reparto propio" },
];
const labelCarrier = (provider) => CARRIERS.find((item) => item.value === provider)?.label || provider;
const newMethod = (provider = "correo_argentino") => ({
  provider, name: `${labelCarrier(provider)} · Envío a domicilio`, enabled: false,
  rateMode: "manual", basePrice: "", freeAbove: "", zones: [],
  localKey: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
});
const newZone = () => ({ label: "", province: "", postalCodePrefix: "", price: "", localKey: `${Date.now()}-${Math.random()}` });
const initial = { revision: 0, pickupEnabled: true, deliveryEnabled: false, methods: [] };
const numericValue = (value) => value === "" || value === null ? 0 : Number(value);

export default function ProjectShippingPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = React.useState(initial);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const result = await shippingApi.get(projectId);
      const shipping = result?.shipping || initial;
      setConfig({ ...initial, ...shipping, methods: shipping.methods?.length
        ? shipping.methods
        : shipping.revision === 0 ? [newMethod("correo_argentino"), newMethod("andreani")] : [] });
    } catch (err) { setError(err.message || "No se pudo cargar la configuración de envíos."); }
    finally { setLoading(false); }
  }, [projectId]);
  React.useEffect(() => { load(); }, [load]);

  const updateMethod = (index, key, value) => setConfig((current) => ({ ...current,
    methods: current.methods.map((method, i) => i === index ? { ...method, [key]: value } : method),
  }));
  const updateZone = (index, zoneIndex, key, value) => setConfig((current) => ({ ...current,
    methods: current.methods.map((method, i) => i === index ? {
      ...method, zones: method.zones.map((zone, j) => j === zoneIndex ? { ...zone, [key]: value } : zone),
    } : method),
  }));
  const removeZone = (index, zoneIndex) => setConfig((current) => ({ ...current,
    methods: current.methods.map((method, i) => i === index ? { ...method, zones: method.zones.filter((_z, j) => j !== zoneIndex) } : method),
  }));
  const addZone = (index) => setConfig((current) => ({ ...current,
    methods: current.methods.map((method, i) => i === index ? { ...method, zones: [...method.zones, newZone()] } : method),
  }));
  const save = async () => {
    setError(""); setSuccess("");
    if (config.methods.some((method) => method.enabled && (method.basePrice === "" || method.zones.some((zone) => zone.price === "")))) {
      setError("Cargá una tarifa base y las tarifas de cada zona activa. Si el envío es gratis, ingresá 0 explícitamente."); return;
    }
    const methods = config.methods.map((method) => ({
      ...(method.id ? { id: method.id } : {}),
      provider: method.provider, name: method.name, enabled: Boolean(method.enabled), rateMode: "manual",
      basePrice: numericValue(method.basePrice), freeAbove: numericValue(method.freeAbove),
      zones: method.zones.map((zone) => ({ label: zone.label, province: zone.province,
        postalCodePrefix: zone.postalCodePrefix, price: numericValue(zone.price) })),
    }));
    if (methods.some((m) => !m.name.trim() || !Number.isFinite(m.basePrice) || m.basePrice < 0 || !Number.isFinite(m.freeAbove) || m.freeAbove < 0 || m.zones.some((z) => !Number.isFinite(z.price) || z.price < 0 || (!z.province.trim() && !z.postalCodePrefix.trim())))) {
      setError("Revisá nombres, tarifas y zonas. No puede haber precios negativos ni zonas sin provincia o CP."); return;
    }
    if (!config.pickupEnabled && !(config.deliveryEnabled && methods.some((method) => method.enabled))) {
      setError("Dejá habilitado retiro o por lo menos un envío activo."); return;
    }
    setSaving(true);
    try {
      const result = await shippingApi.save(projectId, {
        revision: config.revision, pickupEnabled: config.pickupEnabled,
        deliveryEnabled: config.deliveryEnabled, methods,
      });
      setConfig({ ...initial, ...result.shipping });
      setSuccess("Envíos guardados. Las tarifas nuevas se aplican a las próximas compras, no a pedidos ya registrados.");
    } catch (err) { setError(err.message || "No se pudieron guardar los envíos."); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ maxWidth: 1180, mx: "auto", py: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center"><LocalShippingRoundedIcon color="primary" /><Typography variant="h4" sx={{ fontWeight: 950 }}>Envíos y entregas</Typography></Stack>
          <Typography color="text.secondary" sx={{ mt: 0.65 }}>Configurá las empresas, zonas y costos que verá el comprador antes de pagar.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate(`/dashboard/proyectos/${projectId}/pedidos`)}>Ver pedidos</Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={17} color="inherit" /> : <SaveRoundedIcon />} onClick={save} disabled={loading || saving}>Guardar cambios</Button>
        </Stack>
      </Stack>
      {loading ? <Stack direction="row" spacing={2} alignItems="center"><CircularProgress size={24} /> <Typography>Cargando envíos…</Typography></Stack> : <>
        <Alert severity="info" sx={{ mb: 2 }}>Estas son <strong>tarifas manuales del comercio</strong>, no precios oficiales de las transportistas. Para cotizar automáticamente con Andreani o Correo Argentino se necesita un contrato y credenciales de integración, además de peso y medidas de los productos. WebProfit todavía no genera etiquetas ni despachos.</Alert>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}><CardContent>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Formas de entrega</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControlLabel control={<Switch checked={config.pickupEnabled} onChange={(e) => setConfig((cur) => ({ ...cur, pickupEnabled: e.target.checked }))} />} label="Permitir retiro en el local (gratis)" />
            <FormControlLabel control={<Switch checked={config.deliveryEnabled} onChange={(e) => setConfig((cur) => ({ ...cur, deliveryEnabled: e.target.checked }))} />} label="Habilitar envío a domicilio" />
          </Stack>
          <Typography variant="caption" color="text.secondary">Si recién empezás, dejá el retiro activo y cargá las tarifas antes de activar los envíos.</Typography>
        </CardContent></Card>
        {config.methods.map((method, index) => (
          <Card key={method.id || method.localKey || index} variant="outlined" sx={{ mb: 2, borderRadius: 3 }}><CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="h6" sx={{ fontWeight: 900 }}>{labelCarrier(method.provider)}</Typography>
                <Chip size="small" label="Tarifa propia" variant="outlined" />
                <Chip size="small" color={method.enabled ? "success" : "default"} label={method.enabled ? "Visible en checkout" : "Desactivado"} />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FormControlLabel control={<Switch checked={method.enabled} onChange={(e) => updateMethod(index, "enabled", e.target.checked)} />} label="Activo" />
                <IconButton aria-label="Eliminar servicio" onClick={() => setConfig((cur) => ({ ...cur, methods: cur.methods.filter((_m, i) => i !== index) }))}><DeleteOutlineRoundedIcon /></IconButton>
              </Stack>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><TextField fullWidth select label="Empresa" value={method.provider} onChange={(e) => updateMethod(index, "provider", e.target.value)}>{CARRIERS.map((carrier) => <MenuItem value={carrier.value} key={carrier.value}>{carrier.label}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={8}><TextField fullWidth label="Nombre que verá el comprador" value={method.name} onChange={(e) => updateMethod(index, "name", e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required type="number" inputProps={{ min: 0, step: 0.01 }} label="Tarifa base (ARS)" value={method.basePrice} onChange={(e) => updateMethod(index, "basePrice", e.target.value)} helperText="Se usa cuando ninguna zona coincide." /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="number" inputProps={{ min: 0, step: 0.01 }} label="Envío gratis desde (ARS)" value={method.freeAbove} onChange={(e) => updateMethod(index, "freeAbove", e.target.value)} helperText="Dejá vacío o 0 para no ofrecer envío gratis." /></Grid>
            </Grid>
            <Divider sx={{ my: 2.5 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Box><Typography sx={{ fontWeight: 900 }}>Tarifas por destino</Typography><Typography variant="caption" color="text.secondary">Opcionales. Un CP específico prevalece sobre la tarifa provincial; si no coincide, aplica la tarifa base.</Typography></Box>
              <Button size="small" startIcon={<AddRoundedIcon />} disabled={method.zones.length >= 50} onClick={() => addZone(index)}>Zona</Button>
            </Stack>
            {method.zones.map((zone, zoneIndex) => (
              <Grid container spacing={1.25} key={zone.localKey || zoneIndex} alignItems="center" sx={{ mb: 1.25 }}>
                <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Nombre de zona" value={zone.label} onChange={(e) => updateZone(index, zoneIndex, "label", e.target.value)} /></Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Provincia" value={zone.province} onChange={(e) => updateZone(index, zoneIndex, "province", e.target.value)} /></Grid>
                <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="CP empieza con" value={zone.postalCodePrefix} onChange={(e) => updateZone(index, zoneIndex, "postalCodePrefix", e.target.value)} inputProps={{ maxLength: 4 }} /></Grid>
                <Grid item xs={9} sm={3}><TextField fullWidth required size="small" type="number" inputProps={{ min: 0, step: 0.01 }} label="Precio (ARS)" value={zone.price} onChange={(e) => updateZone(index, zoneIndex, "price", e.target.value)} /></Grid>
                <Grid item xs={3} sm={1}><IconButton aria-label="Eliminar zona" onClick={() => removeZone(index, zoneIndex)}><DeleteOutlineRoundedIcon /></IconButton></Grid>
              </Grid>
            ))}
            {!method.zones.length ? <Typography variant="body2" color="text.secondary">Todavía no hay zonas especiales.</Typography> : null}
          </CardContent></Card>
        ))}
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ my: 2 }}>
          {CARRIERS.map((carrier) => <Button key={carrier.value} variant="outlined" startIcon={<AddRoundedIcon />} disabled={config.methods.length >= 20} onClick={() => setConfig((current) => ({ ...current, methods: [...current.methods, newMethod(carrier.value)] }))}>Agregar {carrier.label}</Button>)}
        </Stack>
        <Stack direction="row" justifyContent="flex-end"><Button size="large" variant="contained" startIcon={<SaveRoundedIcon />} onClick={save} disabled={saving}>Guardar envíos</Button></Stack>
      </>}
    </Box>
  );
}
